/*
 * Centraliza configuraciones sensibles/derivadas para rutas protegidas.
 * Paso 2 del plan: ofuscar la ruta admin mediante un slug configurable.
 */

const rawAdminSlug = import.meta.env.VITE_ADMIN_SLUG?.trim();

if (!rawAdminSlug) {
  throw new Error(
    '[security] VITE_ADMIN_SLUG es obligatorio para construir la app. Configura un slug no predecible en tu .env.local. '
  );
}

if (!/^[a-z0-9-]{10,}$/i.test(rawAdminSlug)) {
  throw new Error(
    '[security] VITE_ADMIN_SLUG debe contener al menos 10 caracteres alfanuméricos y guiones para evitar rutas triviales.'
  );
}

// Prefijo de ruta admin completamente ofuscado, sin la palabra "admin" para reducir fingerprinting.
export const ADMIN_ROUTE_SLUG = rawAdminSlug;
export const ADMIN_ROUTE_PREFIX = `/${ADMIN_ROUTE_SLUG}`;
export const AGENT_ROUTE_PREFIX = '/agente';
export const INTERNAL_LOGIN_ROUTE = '/login';
export const INTERNAL_RESTRICTED_ACCESS_ROUTE = '/acceso-restringido';

export const ADMIN_ROUTES = {
  base: ADMIN_ROUTE_PREFIX,
  login: `${ADMIN_ROUTE_PREFIX}/login`,
  mfaSetup: `${ADMIN_ROUTE_PREFIX}/mfa-setup`,
};

export const AGENT_ROUTES = {
  base: AGENT_ROUTE_PREFIX,
  login: `${AGENT_ROUTE_PREFIX}/login`,
};

/**
 * Construye rutas absolutas dentro del árbol admin oculto sin repetir el prefijo.
 * Acepta subrutas con o sin slash inicial.
 */
export const buildAdminRoute = (subPath = ''): string => {
  if (!subPath) return ADMIN_ROUTE_PREFIX;
  const normalized = subPath.startsWith('/') ? subPath.slice(1) : subPath;
  return `${ADMIN_ROUTE_PREFIX}/${normalized}`;
};

export const buildAgentRoute = (subPath = ''): string => {
  if (!subPath) return AGENT_ROUTE_PREFIX;
  const normalized = subPath.startsWith('/') ? subPath.slice(1) : subPath;
  return `${AGENT_ROUTE_PREFIX}/${normalized}`;
};

export const normalizeInternalRouteTarget = (target?: string | null): string | null => {
  if (typeof target !== 'string') {
    return null;
  }

  const trimmed = target.trim();
  if (!trimmed.startsWith('/')) {
    return null;
  }

  if (
    trimmed.startsWith(INTERNAL_LOGIN_ROUTE)
    || trimmed.startsWith('/register')
    || trimmed.startsWith('/auth/callback')
  ) {
    return null;
  }

  return trimmed;
};

export const buildInternalLoginUrl = (
  redirectTarget?: string | null,
  extraParams?: Record<string, string | null | undefined>,
): string => {
  const searchParams = new URLSearchParams();
  const normalizedTarget = normalizeInternalRouteTarget(redirectTarget);

  if (normalizedTarget) {
    searchParams.set('redirect', normalizedTarget);
  }

  if (extraParams) {
    Object.entries(extraParams).forEach(([key, value]) => {
      if (typeof value === 'string' && value.length > 0) {
        searchParams.set(key, value);
      }
    });
  }

  const query = searchParams.toString();
  return query ? `${INTERNAL_LOGIN_ROUTE}?${query}` : INTERNAL_LOGIN_ROUTE;
};

/**
 * Helper utilitario para comprobar si una ruta pertenece al árbol admin seguro.
 * Se usará por componentes como MobileFooterNav o ClearGlobalDarkOnPublic para ocultar pistas visuales.
 */
export const isSecureAdminPath = (pathname: string): boolean => pathname.startsWith(ADMIN_ROUTE_PREFIX);
export const isSecureAgentPath = (pathname: string): boolean => pathname.startsWith(AGENT_ROUTE_PREFIX);
